import { buildColumnsObject } from '@shape/lighthouse-components';
import {
  ExtendedOilData,
  HandleAnalysisChange,
  getDescriptionFromRow,
  isCellSelected,
} from 'components/Evidences/Helpers/helper';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { sortDates } from 'utils';
import { CellContext } from '@tanstack/react-table';
import './AttachOilAnalysisTable.scss';
import { Checkbox, Tooltip } from '@shape-ds/components';

type UseOilAnalysisColumnsProps = {
  data: ExtendedOilData[];
  handleCellSelectionChange?: (params: HandleAnalysisChange) => void;
};

export const useOilAnalysisColumns = ({
  data,
  handleCellSelectionChange,
}: UseOilAnalysisColumnsProps) => {
  const { t } = useTranslation();
  const dinamicColumns = useMemo(() => {
    if (data && data.length > 0) {
      const oilColumns = Object.keys(data[0]);

      return oilColumns.filter((key) => !/[a-zA-Z]/.test(key)).sort(sortDates);
    }

    return [];
  }, [data]);

  const baseColumns = {
    description: {
      enableSorting: false,
      size: 160,
      header: t('monitoring:tables.columns.OIL_ANALYSIS.description'),
      accessorFn: (data: ExtendedOilData) => {
        const descriptionKey = Object.keys(data).find((key) => /[a-zA-Z]/.test(key));
        return descriptionKey ? data[descriptionKey] || '' : '';
      },
      cell: (cell: CellContext<ExtendedOilData, unknown>) => {
        const descriptionValue = cell.getValue() as string;
        return <Tooltip title={descriptionValue}>{descriptionValue}</Tooltip>;
      },
    },
  };

  const buildDateColumnsWithCheckbox = (dinamicColumns: string[]) => {
    const handleCellToggle = (description: string | null, key: string, value: boolean) => {
      if (description && handleCellSelectionChange) {
        handleCellSelectionChange({ description, columnKey: key, value });
      }
    };

    return dinamicColumns.map((key) => ({
      id: key,
      enableSorting: false,
      size: 105,
      header: key,
      cell: (cell: CellContext<ExtendedOilData, unknown>) => {
        const row = cell.row.original;
        const cellValue = key in row ? row[key] : '';
        const description = getDescriptionFromRow(row);
        const isSelected = isCellSelected(row, key);

        return (
          <div
            className={`oil-analysis-cell ${isSelected ? 'selected' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleCellToggle(description, key, !isSelected);
            }}
          >
            {handleCellSelectionChange && (
              <Checkbox
                className={`oil-analysis-checkbox ${isSelected ? 'selected' : ''}`}
                checked={Boolean(isSelected)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleCellToggle(description, key, e.target.checked);
                }}
              />
            )}
            <span className="oil-analysis-cell-value">{cellValue || ''}</span>
          </div>
        );
      },
    }));
  };

  return {
    columns: [
      ...buildColumnsObject<ExtendedOilData>({
        columns: baseColumns,
        getLabel: (key: string) => t(`monitoring:tables.columns.OIL_ANALYSIS.${key}`),
      }),
      ...buildDateColumnsWithCheckbox(dinamicColumns),
    ],
  };
};
